import { Typography } from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import React, { VFC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import globalMessages from 'shared/global/messages';

import { messages } from './messages';
import { AnswerDraft, ScorecardQuestion } from './scorecardFillForm';
import { useStyles } from './styles';
import { ValidationError } from './ValidationError';

const OPTION_LABELS = {
  strong_no: messages.recommendationStrongNo, // eslint-disable-line camelcase
  no: globalMessages.no,
  yes: globalMessages.yes,
  strong_yes: messages.recommendationStrongYes, // eslint-disable-line camelcase
} as const;

export interface RecommendationFieldProps {
  question: ScorecardQuestion;
  answer?: AnswerDraft;
  showError: boolean;
  onChange: (questionId: number, patch: AnswerDraft) => void;
}

// The overall recommendation: one full-width option row, no notes, its own
// error copy. The option keys come from the backend; labels are looked up.
export const RecommendationField: VFC<RecommendationFieldProps> = ({
  question,
  answer,
  showError,
  onChange,
}) => {
  const classes = useStyles();
  const intl = useIntl();

  const labelFor = (option: string) => {
    const message = OPTION_LABELS[option as keyof typeof OPTION_LABELS];
    return message ? intl.formatMessage(message) : option;
  };

  return (
    <div
      className={classes.recommendation}
      data-testid="scorecard-recommendation"
    >
      <Typography variant="body2" className={classes.questionTitle}>
        <FormattedMessage {...messages.recommendationHeading} />
        {question.required && (
          <span className={classes.requiredMark} aria-hidden>
            {' '}
            *
          </span>
        )}
      </Typography>
      <ToggleButtonGroup
        exclusive
        aria-label={intl.formatMessage(messages.recommendationHeading)}
        className={classes.recommendationGroup}
        value={answer?.value ?? null}
        onChange={(_event, value: string | null) =>
          onChange(question.id, { value })
        }
      >
        {question.options.map(option => (
          <ToggleButton
            key={option}
            className={classes.recommendationOption}
            value={option}
          >
            {labelFor(option)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {showError && (
        <ValidationError>
          <FormattedMessage {...messages.chooseRecommendation} />
        </ValidationError>
      )}
    </div>
  );
};
